import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Wrench, Calendar, Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useApiCollection } from "../hooks/useApiCollection";
import { MEDIA } from "../lib/site";
import { toEmbedUrl, hasVideo } from "../lib/video";

const IMAGE_KEY_MAP = {
  hero: MEDIA.hero,
  accident: MEDIA.accident,
  blueprint: MEDIA.blueprint,
  topo: MEDIA.topo,
  drone: MEDIA.drone,
};

const pickCover = (cs) => {
  if (Array.isArray(cs.images) && cs.images.length > 0 && cs.images[0]) {
    return cs.images[0];
  }
  return IMAGE_KEY_MAP[cs.image_key] || MEDIA.hero;
};

export default function CaseStudies() {
  const { t, lang } = useLanguage();
  const { data: cases } = useApiCollection("/case-studies", []);
  const [modal, setModal] = useState(null); // { type: 'video'|'gallery', cs, index? }

  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative bg-[#0d0d0d] border-y border-neutral-900 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">{t.cases.overline}</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">
              {t.cases.title}
            </h2>
            <p className="mt-5 text-neutral-400 text-base sm:text-lg max-w-2xl leading-relaxed">
              {t.cases.subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cases.map((cs, idx) => {
            const title = lang === "ro" ? cs.title_ro : cs.title_en;
            const summary = lang === "ro" ? cs.summary_ro : cs.summary_en;
            const location = lang === "ro" ? cs.location_ro : cs.location_en;
            const cover = pickCover(cs);
            const video = hasVideo(cs.video_url);
            const realImages = Array.isArray(cs.images) ? cs.images.filter(Boolean) : [];

            return (
              <motion.article
                key={cs.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: (idx % 2) * 0.1 }}
                data-testid={`case-card-${cs.slug}`}
                className="group relative bg-[#0A0A0A] border border-neutral-800 hover:border-yellow-500/60 transition-colors overflow-hidden flex flex-col"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                    style={{ backgroundImage: `url(${cover})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-yellow-400">
                    <Calendar className="h-3 w-3" />
                    {cs.year}
                  </div>
                  {location && (
                    <div className="absolute top-4 right-4 flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-neutral-300 bg-black/50 backdrop-blur-sm px-2 py-1">
                      <MapPin className="h-3 w-3" />
                      {location}
                    </div>
                  )}

                  {video && (
                    <button
                      onClick={() => setModal({ type: "video", cs })}
                      data-testid={`case-video-${cs.slug}`}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors"
                      aria-label="Play simulare"
                    >
                      <span className="h-16 w-16 rounded-full bg-yellow-500 text-black flex items-center justify-center shadow-[0_8px_24px_-8px_rgba(234,179,8,0.6)] group-hover:scale-110 transition-transform">
                        <Play className="h-7 w-7 ml-1" fill="currentColor" />
                      </span>
                    </button>
                  )}

                  {realImages.length > 1 && (
                    <button
                      onClick={() => setModal({ type: "gallery", cs, index: 0 })}
                      data-testid={`case-gallery-${cs.slug}`}
                      className="absolute bottom-4 right-4 z-10 font-mono-tech text-[10px] uppercase tracking-[0.2em] bg-black/70 backdrop-blur-sm text-yellow-400 border border-yellow-500/40 px-3 py-1.5 hover:bg-yellow-500 hover:text-black transition-colors"
                    >
                      + {realImages.length - 1} foto
                    </button>
                  )}
                </div>

                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-white text-xl sm:text-2xl leading-tight">
                    {title}
                  </h3>
                  <p className="mt-4 text-sm text-neutral-400 leading-relaxed flex-1">
                    {summary}
                  </p>
                  {cs.tools && cs.tools.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-neutral-900 flex items-start gap-3">
                      <Wrench className="h-3.5 w-3.5 text-yellow-400 mt-0.5 shrink-0" />
                      <div className="flex flex-wrap gap-2">
                        {cs.tools.map((tool) => (
                          <span
                            key={tool}
                            className="font-mono-tech text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-neutral-800 text-neutral-300"
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>

      {modal && <MediaModal modal={modal} onClose={() => setModal(null)} lang={lang} />}
    </section>
  );
}

const MediaModal = ({ modal, onClose, lang }) => {
  const { type, cs } = modal;
  const [idx, setIdx] = useState(modal.index || 0);
  const realImages = Array.isArray(cs.images) ? cs.images.filter(Boolean) : [];
  const title = lang === "ro" ? cs.title_ro : cs.title_en;

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (type === "gallery") {
        if (e.key === "ArrowRight") setIdx((i) => (i + 1) % realImages.length);
        if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + realImages.length) % realImages.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [type, realImages.length, onClose]);

  return (
    <div
      data-testid="media-modal"
      className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        data-testid="media-modal-close"
        className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center bg-neutral-900 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black transition-colors z-10"
        aria-label="Închide"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className="relative w-full max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-yellow-400 mb-3">
          {type === "video" ? (lang === "ro" ? "Simulare video" : "Video simulation") : (lang === "ro" ? "Galerie foto" : "Photo gallery")}
        </div>
        <div className="font-display font-semibold text-white text-xl sm:text-2xl mb-5">{title}</div>

        {type === "video" && (
          <div className="aspect-video bg-black border border-neutral-800 overflow-hidden">
            <iframe
              src={toEmbedUrl(cs.video_url)}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        )}

        {type === "gallery" && realImages.length > 0 && (
          <div className="relative">
            <div className="aspect-video bg-black border border-neutral-800 overflow-hidden flex items-center justify-center">
              <img
                src={realImages[idx]}
                alt={`${title} - ${idx + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
            {realImages.length > 1 && (
              <>
                <button
                  onClick={() => setIdx((i) => (i - 1 + realImages.length) % realImages.length)}
                  className="absolute top-1/2 -translate-y-1/2 left-3 h-11 w-11 bg-black/70 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black flex items-center justify-center"
                  aria-label="Anterior"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setIdx((i) => (i + 1) % realImages.length)}
                  className="absolute top-1/2 -translate-y-1/2 right-3 h-11 w-11 bg-black/70 border border-neutral-700 text-white hover:bg-yellow-500 hover:text-black flex items-center justify-center"
                  aria-label="Următor"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="mt-4 flex items-center justify-center gap-1.5">
                  {realImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={`h-1.5 transition-all ${
                        i === idx ? "w-8 bg-yellow-500" : "w-2.5 bg-neutral-700 hover:bg-neutral-500"
                      }`}
                      aria-label={`Foto ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
